import { ErrorSubcode, type DeliverableFailureReason } from "@/types";

export type ErrorSuffix = "support" | "retry" | "none";
export type ErrorSeverity = "error" | "warning";

export interface ErrorEntry {
  message: string;
  suffix?: ErrorSuffix;
  severity?: ErrorSeverity;
}

export type SubcodeMessages = Partial<Record<ErrorSubcode, ErrorEntry>>;

export type FeatureArea =
  | "phone"
  | "checkout"
  | "client"
  | "conversation"
  | "meetingPrep"
  | "invitationEmails"
  | "settings"
  | "integrations"
  | "crm"
  | "auth"
  | "mfa"
  | "customization"
  | "admin"
  | "audioTranscription"
  | "sharing"
  | "askFocal"
  | "videoMeeting"
  | "deliverables"
  | "formFill";

export const defaultMessages: SubcodeMessages = {
  // Validation
  [ErrorSubcode.MISSING_FIELD]: {
    message: "A required field is missing. Please check your input.",
  },
  [ErrorSubcode.INVALID_FORMAT]: {
    message: "The format is invalid. Please check your input.",
  },
  [ErrorSubcode.INVALID_VALUE]: {
    message: "The provided value is not valid.",
  },
  [ErrorSubcode.TOO_LONG]: { message: "The value is too long." },
  [ErrorSubcode.TOO_SHORT]: { message: "The value is too short." },
  [ErrorSubcode.INVALID_TYPE]: {
    message: "An invalid value type was provided.",
  },
  [ErrorSubcode.VALIDATION_ERROR]: {
    message:
      "The provided data is invalid. Please check your input and try again.",
  },
  [ErrorSubcode.INVALID_EMAIL_DOMAIN]: {
    message:
      "Please use a business email address. Personal email domains are not supported.",
  },

  // Auth
  [ErrorSubcode.INVALID_CREDENTIALS]: {
    message: "Your credentials are invalid or have expired.",
    suffix: "support",
  },
  [ErrorSubcode.UNAUTHORIZED]: {
    message: "Your session has expired. Please log in again.",
  },
  [ErrorSubcode.FORBIDDEN]: {
    message: "You don't have permission to perform this action.",
  },
  [ErrorSubcode.TOKEN_EXPIRED]: {
    message: "Your session has expired. Please log in again.",
  },
  [ErrorSubcode.SESSION_INVALID]: {
    message: "Your session is no longer valid. Please log in again.",
  },

  // Resource
  [ErrorSubcode.NOT_FOUND]: {
    message: "The requested item could not be found.",
  },
  [ErrorSubcode.ALREADY_EXISTS]: { message: "This already exists." },
  [ErrorSubcode.CONFLICT]: {
    message: "This action conflicts with the current state.",
    suffix: "retry",
  },
  [ErrorSubcode.RESOURCE_LIMIT]: {
    message: "You've reached the limit for this resource.",
  },

  // Operation
  [ErrorSubcode.OPERATION_FAILED]: {
    message: "The operation could not be completed.",
    suffix: "support",
  },
  [ErrorSubcode.INVALID_STATE]: {
    message: "This action can't be performed right now.",
    suffix: "retry",
  },
  [ErrorSubcode.PRECONDITION_FAILED]: {
    message: "A required condition was not met.",
    suffix: "retry",
  },

  // External
  [ErrorSubcode.EXTERNAL_SERVICE_ERROR]: {
    message: "A third-party service encountered an error.",
    suffix: "support",
  },
  [ErrorSubcode.TIMEOUT]: {
    message: "The request timed out.",
    suffix: "retry",
  },
  [ErrorSubcode.RATE_LIMIT_EXCEEDED]: {
    message: "Too many requests.",
    suffix: "retry",
  },

  // Catch-all
  [ErrorSubcode.UNKNOWN_ERROR]: {
    message: "Something went wrong.",
    suffix: "support",
  },
  [ErrorSubcode.INTERNAL_ERROR]: {
    message: "An internal error occurred.",
    suffix: "support",
  },
};

// A deliverable chat turn that ended without agent-service saying how it ended.
// Not a failed edit — nothing was attempted — so the copy says what the advisor
// needs to know: the request did not land and the document is untouched.
//
// Exported rather than written inline in the registry entry below so the specs
// that assert this copy cannot drift from it.
export const DELIVERABLE_NO_TURN_OUTCOME =
  "I could not settle that request. Nothing was changed.";

// Copy for a deliverable whose server-owned build ended without a document.
//
// A terminal row names how it failed with a `failureReason` enum and, sometimes,
// a machine-shaped `failureDetail`. The advisor-facing words for all of it live
// here with the rest of the error copy — the reason labels the list rows and the
// status toasts read, plus the viewer's heading and its fallback sentence — and
// `deliverableLifecycle.ts` only selects between them and formats the detail.
export const DELIVERABLE_FAILURE_REASON_LABELS: Partial<
  Record<DeliverableFailureReason, string>
> = {
  AGENT_LIMIT_REACHED: "Generation limit reached",
  // What agent-service actually sends for anything it did not classify —
  // including every document-service outage, whose own
  // DOCUMENT_SERVICE_UNREACHABLE reason its worker discards. Mapped
  // explicitly so this reads as an expected outcome rather than falling
  // through the unknown-reason path.
  BUILD_FAILED: "Generation failed",
  DOCUMENT_SERVICE_UNREACHABLE: "Document checks unavailable",
  INSPECTION_FAILED: "Document checks failed",
  VALIDATION_FAILED: "Needs review",
  UNKNOWN: "Generation failed",
};

/**
 * Used when a row names no failure reason, or names one Focal has no words for
 * yet. Deliberately generic: the enum itself is an internal name, and
 * title-casing an unmapped one ("Build Failed", "Artifact Too Large") puts
 * Focal's internals in front of an advisor.
 */
export const DELIVERABLE_FAILURE_REASON_FALLBACK = "Generation failed";

/**
 * The sentence under the failure heading, keyed on the same reason enum as the
 * labels above. Each says what happened and what to do about it.
 *
 * A terminal row also carries a machine-shaped `failureDetail` — service names,
 * HTTP verbs and endpoint paths. That is for support and internal admin, never
 * for this surface: "DocumentServiceError: document-service unreachable on POST
 * /document/deliverable/inspect-artifact" tells an advisor nothing they can act
 * on, so no code path renders it into advisor-facing copy.
 */
export const DELIVERABLE_FAILURE_REASON_DESCRIPTIONS: Partial<
  Record<DeliverableFailureReason, string>
> = {
  AGENT_LIMIT_REACHED:
    "Your firm has used all of its generations for now. Contact support to raise the limit.",
  BUILD_FAILED:
    "Focal could not finish writing the document. If this keeps happening, contact support.",
  DOCUMENT_SERVICE_UNREACHABLE:
    "Focal could not complete the document checks. This is usually temporary.",
  INSPECTION_FAILED:
    "Focal could not verify the generated document. Review it before sharing it with a client.",
  VALIDATION_FAILED:
    "The document was written, but it did not pass Focal's checks. Read it through before you show it to a client.",
};

/**
 * Shown when the reason has no description of its own.
 *
 * These sentences say what happened; the buttons beside them say what to do.
 * Keeping the two apart matters because the actions on offer depend on who is
 * looking — a colleague viewing a shared deliverable they do not own gets no
 * "Try again", so copy that told them to try again would name a button that is
 * not there.
 */
export const DELIVERABLE_FAILURE_DESCRIPTION_FALLBACK =
  "Focal could not finish writing the document. If this keeps happening, contact support.";

/** Heading shown in the viewer when a build saved no document. */
export const DELIVERABLE_GENERATION_FAILED_TITLE =
  "This deliverable could not be generated";

/** Shown under that heading when the row carries no failure copy of its own. */
export const DELIVERABLE_GENERATION_FAILED_FALLBACK =
  "Generation failed before a document was saved.";

export const featureMessages: Record<FeatureArea, SubcodeMessages> = {
  formFill: {
    [ErrorSubcode.RESOURCE_LIMIT]: {
      message: "This form exceeds the AI fill input limit.",
      suffix: "none",
    },
    [ErrorSubcode.TIMEOUT]: {
      message: "The AI fill service timed out.",
      suffix: "retry",
    },
    [ErrorSubcode.EXTERNAL_SERVICE_ERROR]: {
      message: "The AI fill service is unavailable.",
      suffix: "retry",
    },
  },
  invitationEmails: {
    [ErrorSubcode.INVALID_VALUE]: {
      message:
        "The email address or verification code is invalid, or the code has expired. Check the address or resend verification for a new code.",
    },
    [ErrorSubcode.TOKEN_EXPIRED]: {
      message:
        "This verification code has expired. Resend verification for a new code.",
    },
    [ErrorSubcode.CONFLICT]: {
      message:
        "This address is already registered or claimed by another firm. Contact support@meetwithfocal.com if you need help.",
    },
    [ErrorSubcode.RATE_LIMIT_EXCEEDED]: {
      message: "Please wait before requesting another verification email.",
    },
    [ErrorSubcode.UNKNOWN_ERROR]: {
      message: "Unable to load or update invitation emails.",
      suffix: "retry",
    },
  },
  phone: {
    [ErrorSubcode.ALREADY_EXISTS]: {
      message: "This phone number is already registered.",
    },
  },
  checkout: {
    [ErrorSubcode.ALREADY_EXISTS]: {
      message: "An account with this email already exists.",
    },
    [ErrorSubcode.INVALID_EMAIL_DOMAIN]: {
      message:
        "Please use a business email address. Personal email domains are not supported.",
    },
    [ErrorSubcode.MISSING_FIELD]: {
      message: "Required checkout information is missing.",
      suffix: "support",
    },
    [ErrorSubcode.INVALID_VALUE]: {
      message: "The checkout session is invalid.",
      suffix: "support",
    },
    [ErrorSubcode.RATE_LIMIT_EXCEEDED]: {
      message: "Too many attempts. Please wait before trying again.",
    },
  },
  client: {
    [ErrorSubcode.NOT_FOUND]: {
      message: "This client could not be found.",
    },
    [ErrorSubcode.ALREADY_EXISTS]: {
      message: "A client with this information already exists.",
    },
    [ErrorSubcode.CONFLICT]: {
      message:
        "Another client already uses this as their primary. It was saved as a secondary instead.",
      severity: "warning",
    },
    [ErrorSubcode.VALIDATION_ERROR]: {
      message: "Please check the client details and try again.",
    },
  },
  conversation: {
    [ErrorSubcode.NOT_FOUND]: {
      message: "This conversation could not be found.",
    },
    [ErrorSubcode.ALREADY_EXISTS]: {
      message: "This speaker assignment already exists.",
    },
    [ErrorSubcode.CONFLICT]: {
      message: "This conversation was updated by someone else.",
      suffix: "retry",
    },
  },
  meetingPrep: {
    [ErrorSubcode.NOT_FOUND]: {
      message: "This meeting prep could not be found.",
    },
    [ErrorSubcode.OPERATION_FAILED]: {
      message: "Failed to generate meeting prep.",
      suffix: "retry",
    },
  },
  settings: {
    [ErrorSubcode.OPERATION_FAILED]: {
      message: "Failed to update settings.",
      suffix: "retry",
    },
  },
  integrations: {
    [ErrorSubcode.INTEGRATION_PERMISSIONS_MISSING]: {
      message:
        "Focal wasn't given every permission it needs. Connect again and allow all the requested access.",
    },
    [ErrorSubcode.OAUTH_GRANT_INVALID]: {
      message: "That authorization expired before we could use it.",
      suffix: "retry",
    },
    [ErrorSubcode.INVALID_CREDENTIALS]: {
      message:
        "Your integration credentials are invalid or have expired. Please reconnect.",
    },
    [ErrorSubcode.EXTERNAL_SERVICE_ERROR]: {
      message: "The integration service encountered an error.",
      suffix: "support",
    },
    [ErrorSubcode.OPERATION_FAILED]: {
      message: "Failed to connect the integration.",
      suffix: "support",
    },
    [ErrorSubcode.FORBIDDEN]: {
      message:
        "Could not connect your account. Please contact your IT admin for permissions.",
    },
    [ErrorSubcode.UNAUTHORIZED]: {
      message:
        "Could not connect your account. Please contact your IT admin for permissions.",
    },
  },
  crm: {
    [ErrorSubcode.INVALID_CREDENTIALS]: {
      message:
        "Your CRM credentials are invalid or have expired. Please reconnect your CRM.",
    },
    [ErrorSubcode.EXTERNAL_SERVICE_ERROR]: {
      message: "The CRM service encountered an error.",
      suffix: "support",
    },
    [ErrorSubcode.MISSING_FIELD]: {
      message:
        "Required CRM fields are missing. Please check your CRM configuration.",
    },
    [ErrorSubcode.INVALID_VALUE]: {
      message: "The CRM configuration contains invalid values.",
    },
    [ErrorSubcode.NOT_FOUND]: {
      message: "The CRM record could not be found. It may have been deleted.",
    },
    [ErrorSubcode.UNAUTHORIZED]: {
      message: "CRM authentication failed. Please reconnect your CRM.",
    },
  },
  auth: {
    [ErrorSubcode.PASSWORD_BREACHED]: {
      message:
        "This password has appeared in a data breach. Please choose a different password.",
    },
    [ErrorSubcode.INVALID_CREDENTIALS]: {
      message: "Invalid email or password.",
    },
    [ErrorSubcode.TOKEN_EXPIRED]: {
      message: "Your reset link has expired. Please request a new one.",
    },
    [ErrorSubcode.SESSION_INVALID]: {
      message: "Your session is no longer valid. Please log in again.",
    },
    [ErrorSubcode.RATE_LIMIT_EXCEEDED]: {
      message: "Too many login attempts. Please wait before trying again.",
    },
    [ErrorSubcode.OPERATION_FAILED]: {
      message: "Authentication failed.",
      suffix: "support",
    },
    [ErrorSubcode.NOT_FOUND]: {
      message: "Invalid reset token. Please request a new password reset.",
    },
    [ErrorSubcode.FORBIDDEN]: {
      message: "Authorization method not supported for this organization.",
      suffix: "support",
    },
  },
  mfa: {
    [ErrorSubcode.INVALID_CREDENTIALS]: {
      message: "Invalid code.",
      suffix: "retry",
    },
    [ErrorSubcode.VALIDATION_ERROR]: {
      message: "Invalid code.",
      suffix: "retry",
    },
    [ErrorSubcode.OPERATION_FAILED]: {
      message:
        "That code isn't valid. Check your authenticator app and try again.",
      suffix: "support",
    },
    [ErrorSubcode.NOT_FOUND]: {
      message: "MFA configuration not found.",
      suffix: "support",
    },
    [ErrorSubcode.RATE_LIMIT_EXCEEDED]: {
      message: "Too many attempts. Please wait before trying again.",
    },
  },
  customization: {
    [ErrorSubcode.ALREADY_EXISTS]: {
      message:
        "This customization already exists. Refresh the page if you expected to edit it.",
    },
    [ErrorSubcode.CONFLICT]: {
      message:
        "This customization changed elsewhere. Refresh the page and try again.",
    },
    [ErrorSubcode.OPERATION_FAILED]: {
      message: "Failed to save customization.",
      suffix: "retry",
    },
    [ErrorSubcode.VALIDATION_ERROR]: {
      message: "The template content is invalid. Please check and try again.",
    },
  },
  admin: {
    [ErrorSubcode.ALREADY_EXISTS]: {
      message: "A user with this email already exists.",
    },
    [ErrorSubcode.INVALID_STATE]: {
      message: "The organization is not properly configured.",
      suffix: "support",
    },
    [ErrorSubcode.EXTERNAL_SERVICE_ERROR]: {
      message: "Failed to send the invitation.",
      suffix: "retry",
    },
    [ErrorSubcode.OPERATION_FAILED]: {
      message: "Failed to invite the user.",
      suffix: "retry",
    },
  },
  audioTranscription: {
    [ErrorSubcode.INVALID_VALUE]: {
      message: "The audio file is not in a supported format.",
    },
    [ErrorSubcode.OPERATION_FAILED]: {
      message: "Transcription failed.",
      suffix: "retry",
    },
  },
  sharing: {
    [ErrorSubcode.FORBIDDEN]: {
      message: "You don't have permission to share this item.",
    },
    [ErrorSubcode.NOT_FOUND]: {
      message: "The item you're trying to share could not be found.",
    },
  },
  askFocal: {
    [ErrorSubcode.OPERATION_FAILED]: {
      message: "Something went wrong with Ask Focal.",
      suffix: "retry",
    },
  },
  videoMeeting: {
    [ErrorSubcode.INVALID_VALUE]: {
      message:
        "That meeting link isn't supported. Paste a Zoom, Microsoft Teams, or Google Meet link.",
    },
  },
  deliverables: {
    [ErrorSubcode.NOT_FOUND]: {
      message: "This deliverable no longer exists or was not shared with you.",
    },
    [ErrorSubcode.FORBIDDEN]: {
      message: "You don't have permission to change this deliverable.",
    },
    [ErrorSubcode.OPERATION_FAILED]: {
      message: "Something went wrong with this deliverable.",
      suffix: "retry",
    },
    [ErrorSubcode.CONFLICT]: {
      message: "A build is already running for this deliverable.",
      suffix: "retry",
    },
    [ErrorSubcode.INVALID_STATE]: {
      message: "This deliverable cannot be generated right now.",
      suffix: "retry",
    },
    [ErrorSubcode.PRECONDITION_FAILED]: {
      // No retry suffix: the same request fails the same way until the advisor
      // fixes the inputs, so the message carries the corrective action instead.
      message:
        "This deliverable is missing required build inputs. Add a file, a client, a meeting or a CRM note, then generate it again.",
      suffix: "none",
    },
    [ErrorSubcode.VALIDATION_ERROR]: {
      message: "Please check the deliverable details and try again.",
    },
    [ErrorSubcode.RESOURCE_LIMIT]: {
      message: "That file is too large. Please upload a smaller file.",
    },
    [ErrorSubcode.TIMEOUT]: {
      message: "This took too long to finish.",
      suffix: "retry",
    },
    [ErrorSubcode.EXTERNAL_SERVICE_ERROR]: {
      message: "We could not reach the service needed for this deliverable.",
      suffix: "retry",
    },
    // The chat stream ended without a turn outcome. This gets its own subcode
    // rather than reusing `INVALID_STATE`, which the backend does send: parking
    // chat-specific copy on a generic subcode would answer every other
    // deliverables call site ("Failed to save template.", "Failed to download
    // the PDF.") with a sentence about an unsettled chat turn.
    //
    // Deliberately no "please try again" suffix — the chat composer is right
    // there, and the conversational surface asks again by being typed into,
    // not by a retry prompt.
    [ErrorSubcode.NO_TURN_OUTCOME]: {
      message: DELIVERABLE_NO_TURN_OUTCOME,
    },
  },
};

// Inline confirmation copy shown in the chat when the user types yes/no
// after the pending interrupt has already been abandoned.
//
// Why an interrupt gets abandoned: LangGraph's `interrupt()` pauses the
// agent and waits for a `Command(resume="yes"|"no")` to continue. If the
// next user message isn't a yes/no (e.g. they ask "what's my summary
// first?" or "actually nevermind, do X instead"), the server sends it as
// a fresh user turn instead of a resume — and LangGraph treats that as
// "moving on," dropping the pending interrupt without an explicit answer.
// The user's later "yes" then targets an id that no longer exists →
// server returns 409 → this copy shows inline.
export const ASK_FOCAL_INTERRUPT_NOT_PENDING =
  "I didn't catch that in time. Just let me know what you'd like and I'll do it.";

export const ASK_FOCAL_CARD_ALREADY_HANDLED =
  "This change was already handled. You don't need to send it again.";

export const deliverableEditFailureMessages = {
  failed:
    "That edit did not go through. The saved document is unchanged. Try the request again, or contact support if it keeps failing.",
  unavailable:
    "The document assistant is temporarily unavailable. The saved document is unchanged. Try again later, or contact support.",
  missingData:
    "There is not enough source information to make this edit. The saved document is unchanged. Attach the missing information or explain which source to use, then send the request again.",
  tooLarge:
    "The assistant could not finish this request in one turn. The saved document is unchanged. Try one section or change at a time, or contact support.",
  validation:
    "This edit could not pass the document checks. The saved document is unchanged. Try a smaller, more specific change, or contact support.",
  access:
    "This request could not be authorized. Reload the page and check that you still have access before trying again.",
  notSaved:
    "The edit finished, but we could not confirm it was saved. Reload the page and check the latest document before trying the edit again.",
  attachmentSaved:
    "Your document changes were saved, but the attachments could not be stored for future requests. Use Retry attachments to save only the files, or contact support. Do not repeat the saved edit.",
  attachmentAnswered:
    "Your request was answered, but the attachments could not be stored for future requests. Use Retry attachments to save only the files, or contact support.",
  attachmentsRecovered:
    "Your attachments are now saved for future requests. The original request was not repeated.",
  history:
    "We could not load this document's history. Reload history to continue. If it still fails, contact support.",
} as const;
